import {Link, createFileRoute} from '@tanstack/react-router'
import {useSuspenseQuery} from '@tanstack/react-query'
import {useTranslations} from 'use-intl'
import {useMemo} from 'react'
import {DesignCollection} from '~/client/spreadshop/missing-types'
import {collectionsQueryOptions} from '~/client/fn/collections'
import ImageComposition from '~/components/ImageComposition'
import Breadcrumbs from '~/components/common/Breadcrumbs'
import useLocaleParams from '~/hooks/useLocaleParams'
import Card from '~/components/common/Card'

export const Route = createFileRoute('/$lang/_layout/collections/')({
    component: RouteComponent,
    loader: async ({context}) => {
        await context.queryClient.ensureQueryData(collectionsQueryOptions(context.locale))
    },
})

function RouteComponent() {
    const params = useLocaleParams()
    const t = useTranslations('collectionList')
    const {data: collections} = useSuspenseQuery(collectionsQueryOptions(params.locale))

    return (
        <div className="px-4 py-6 mx-auto max-w-screen-xl lg:py-12 sm:px-6">
            <Breadcrumbs
                className="mb-2 md:mb-6"
                items={[
                    {name: t('breadcrumbs.home'), to: '/$lang', params: params},
                    {name: t('breadcrumbs.collections'), to: '.'},
                ]}
            />
            <h1 className="sr-only">{t('title')}</h1>
            <div className="space-y-10 md:space-y-16">
                {collections.map((collection) => (
                    <CollectionPreview key={collection.id} collection={collection} />
                ))}
            </div>
        </div>
    )
}

type CollectionPreviewProps = {
    collection: DesignCollection
}

const CollectionPreview = ({collection}: CollectionPreviewProps) => {
    const params = useLocaleParams()
    const t = useTranslations('collectionList')
    const firstDesigns = useMemo(() => collection.designs.slice(0, 3), [collection.designs])

    return (
        <div>
            <div className="mb-3 lg:mb-5">
                <h2 className="text-2xl font-medium">{collection.name}</h2>
                {!!collection.description?.length && (
                    <p className="text-gray-400">{collection.description}</p>
                )}
            </div>
            <ul className="grid grid-cols-2 gap-4 lg:gap-6 md:grid-cols-4">
                {firstDesigns.map((design) => (
                    <li key={design?.id}>
                        <Link
                            className="block aspect-square h-full"
                            to="/$lang/products"
                            params={params}
                            search={{design: design?.id}}
                        >
                            <Card className="p-2 md:p-4 flex flex-col gap-2 justify-between items-center h-full">
                                <div className="h-full w-auto px-4">
                                    <ImageComposition
                                        image={design?.image!}
                                        sizes="400px"
                                        className="select-none"
                                    />
                                </div>
                                <div className="text-center font-bold text-sm">{design?.name}</div>
                            </Card>
                        </Link>
                    </li>
                ))}
                {collection.designs.length > 3 && (
                    <li>
                        <Link
                            className="block aspect-square h-full"
                            to="/$lang/collections/$collectionId"
                            params={{...params, collectionId: collection.id}}
                        >
                            <Card className="p-2 md:p-4 flex flex-col gap-2 justify-between items-center h-full">
                                <div className="w-full h-full p-2 relative">
                                    <div className="p-2 rounded-md border border-gray-200 border-dashed absolute inset-0 h-full w-full">
                                        <svg
                                            className="block h-full w-auto mx-auto"
                                            xmlns="http://www.w3.org/2000/svg"
                                            fill="none"
                                            viewBox="0 0 24 24"
                                            stroke="currentColor"
                                        >
                                            <path
                                                className="animate-pulse motion-reduce:animate-none "
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth="0.75"
                                                strokeDasharray="0.5 1"
                                                strokeDashoffset="0.25"
                                                d="M9.813 15.904 9 18.75l-.813-2.846a4.5 4.5 0 0 0-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 0 0 3.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 0 0 3.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 0 0-3.09 3.09Z"
                                            />
                                            <path
                                                className="animate-pulse motion-reduce:animate-none delay-1000 "
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth="0.5"
                                                strokeDasharray="0.25 0.75"
                                                strokeDashoffset="0"
                                                d="M18.259 8.715 18 9.75l-.259-1.035a3.375 3.375 0 0 0-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 0 0 2.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 0 0 2.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 0 0-2.456 2.456Z"
                                            />
                                            <path
                                                className="animate-pulse motion-reduce:animate-none delay-500 "
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth="0.35"
                                                strokeDasharray="0.2 0.5"
                                                strokeDashoffset="0.25"
                                                d="M16.894 20.567 16.5 21.75l-.394-1.183a2.25 2.25 0 0 0-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 0 0 1.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 0 0 1.423 1.423l1.183.394-1.183.394a2.25 2.25 0 0 0-1.423 1.423Z"
                                            />
                                        </svg>
                                    </div>
                                </div>
                                <div className="text-center font-bold text-sm">{t('showMore')}</div>
                            </Card>
                        </Link>
                    </li>
                )}
            </ul>
        </div>
    )
}
